const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

class ApiService {
    constructor() {
        this.baseURL = API_BASE_URL;
        this.token = localStorage.getItem('authToken');
        this.currentPropertyId = localStorage.getItem('currentPropertyId');
    }

    // Set authentication token
    setToken(token) {
        this.token = token;
        localStorage.setItem('authToken', token);
    }

    // Clear authentication token
    clearToken() {
        this.token = null;
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentPropertyId');
    }

    // Set current property ID
    setCurrentPropertyId(propertyId) {
        this.currentPropertyId = propertyId;
        localStorage.setItem('currentPropertyId', propertyId);
    }

    // Get current property ID
    getCurrentPropertyId() {
        return this.currentPropertyId;
    }

    // Get headers for API requests
    getHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        };

        if (this.token) {
            headers['Authorization'] = `Token ${this.token}`;
        }

        return headers;
    }

    // Make API request
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const config = {
            headers: this.getHeaders(),
            ...options,
        };

        try {
            const response = await fetch(url, config);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            // Handle 204 No Content responses (common for DELETE operations)
            if (response.status === 204) {
                return { success: true, message: 'Resource deleted successfully' };
            }

            // Only try to parse JSON if there's content
            if (response.headers.get('content-length') === '0' || response.headers.get('content-length') === null) {
                return { success: true };
            }

            return await response.json();
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    // Authentication methods
    async googleSignIn(credential) {
        const response = await this.request('/api/auth/google/', {
            method: 'POST',
            body: JSON.stringify({ credential }),
        });

        if (response.token) {
            this.setToken(response.token);
        }

        return response;
    }

    async checkAuthStatus() {
        if (!this.token) {
            return { authenticated: false };
        }

        try {
            return await this.request('/api/auth/status/');
        } catch (error) {
            this.clearToken();
            return { authenticated: false };
        }
    }

    // Property methods
    async getProperties() {
        return await this.request('/api/properties/');
    }

    async createProperty(propertyData) {
        const response = await this.request('/api/properties/', {
            method: 'POST',
            body: JSON.stringify(propertyData),
        });

        // Set as current property if it's the first one
        if (response.id && !this.currentPropertyId) {
            this.setCurrentPropertyId(response.id);
        }

        return response;
    }

    async getOrCreateDefaultProperty() {
        try {
            const properties = await this.getProperties();

            if (properties.results && properties.results.length > 0) {
                // Use the first property (prevent duplicates)
                const property = properties.results[0];
                this.setCurrentPropertyId(property.id);
                console.log(`Using existing property: ${property.name} (ID: ${property.id})`);
                return property;
            } else {
                // Only create a default property if none exist
                console.log('No properties found, creating default property...');
                const defaultProperty = {
                    name: 'My Property',
                    property_type: 'apartment',
                    address: 'Default Address',
                    city: 'Default City',
                    state: 'Default State',
                    pincode: '000000',
                    area_sqft: 1000,
                    purchase_date: new Date().toISOString().split('T')[0]
                };

                const newProperty = await this.createProperty(defaultProperty);
                console.log(`Created new property: ${newProperty.name} (ID: ${newProperty.id})`);
                return newProperty;
            }
        } catch (error) {
            console.error('Error getting or creating default property:', error);
            throw error;
        }
    }

    async updateProperty(propertyId, propertyData) {
        return await this.request(`/api/properties/${propertyId}/`, {
            method: 'PUT',
            body: JSON.stringify(propertyData),
        });
    }

    async deleteProperty(propertyId) {
        return await this.request(`/api/properties/${propertyId}/`, {
            method: 'DELETE',
        });
    }

    async calculatePropertyReturns(propertyId) {
        return await this.request(`/api/properties/${propertyId}/calculate_returns/`, {
            method: 'POST',
        });
    }

    // Transaction methods
    async getTransactions(propertyId = null) {
        const targetPropertyId = propertyId || this.currentPropertyId;
        if (!targetPropertyId) {
            throw new Error('No property selected');
        }

        const endpoint = `/api/transactions/?property=${targetPropertyId}`;
        return await this.request(endpoint);
    }

    async createTransaction(transactionData) {
        const targetPropertyId = this.currentPropertyId;
        if (!targetPropertyId) {
            throw new Error('No property selected');
        }

        const dataWithProperty = {
            ...transactionData,
            property_id: targetPropertyId
        };

        return await this.request('/api/transactions/', {
            method: 'POST',
            body: JSON.stringify(dataWithProperty),
        });
    }

    async updateTransaction(transactionId, transactionData) {
        return await this.request(`/api/transactions/${transactionId}/`, {
            method: 'PUT',
            body: JSON.stringify(transactionData),
        });
    }

    async deleteTransaction(transactionId) {
        return await this.request(`/api/transactions/${transactionId}/`, {
            method: 'DELETE',
        });
    }

    // Tax calculation methods
    async getTaxCalculations(propertyId = null) {
        const targetPropertyId = propertyId || this.currentPropertyId;
        if (!targetPropertyId) {
            throw new Error('No property selected');
        }

        const endpoint = `/api/tax-calculations/?property=${targetPropertyId}`;
        return await this.request(endpoint);
    }

    async createTaxCalculation(taxData) {
        return await this.request('/api/tax-calculations/', {
            method: 'POST',
            body: JSON.stringify(taxData),
        });
    }

    // NEW: Backend tax calculation endpoint
    async calculateTax(acquisitionDate, transferDate, taxSettings) {
        const targetPropertyId = this.currentPropertyId;
        if (!targetPropertyId) {
            throw new Error('No property selected');
        }

        const requestData = {
            property_id: targetPropertyId,
            acquisition_date: acquisitionDate,
            transfer_date: transferDate,
            tax_settings: taxSettings
        };

        try {
            return await this.request('/api/tax-calculations/calculate/', {
                method: 'POST',
                body: JSON.stringify(requestData),
            });
        } catch (error) {
            console.error('Tax calculation API failed, using mock calculation:', error);

            // Mock tax calculation for development when API is not accessible
            const netSaleProceeds = parseFloat(taxSettings.net_sale_proceeds) || 8000000;
            const purchasePrice = 5000000; // Mock value
            const capitalGain = netSaleProceeds - purchasePrice;
            const taxRate = taxSettings.regime === 'old' ? 0.20 : 0.125;
            const taxPayable = capitalGain * taxRate;

            return {
                capital_gain: capitalGain,
                tax_payable: taxPayable,
                regime_used: taxSettings.regime,
                calculation_type: 'MOCK_CALCULATION',
                indexed_cost: purchasePrice,
                net_sale_proceeds: netSaleProceeds,
                success: true
            };
        }
    }

    // Cost Inflation Index methods
    async getCostInflationIndices() {
        return await this.request('/api/cost-inflation-index/');
    }

    // User methods
    async getUserProfile() {
        return await this.request('/api/users/');
    }

    async updateUserProfile(userData) {
        return await this.request('/api/users/', {
            method: 'PUT',
            body: JSON.stringify(userData),
        });
    }

    // Subscription methods
    async getSubscriptionPlans() {
        return await this.request('/api/subscriptions/plans/');
    }

    async getSubscriptionStatus() {
        return await this.request('/api/subscriptions/status/');
    }

    async createSubscriptionOrder(planType) {
        return await this.request('/api/subscriptions/create-order/', {
            method: 'POST',
            body: JSON.stringify({ plan_type: planType }),
        });
    }

    async verifyPayment(razorpayOrderId, razorpayPaymentId, razorpaySignature) {
        return await this.request('/api/subscriptions/verify-payment/', {
            method: 'POST',
            body: JSON.stringify({
                razorpay_order_id: razorpayOrderId,
                razorpay_payment_id: razorpayPaymentId,
                razorpay_signature: razorpaySignature,
            }),
        });
    }

    async cancelSubscription() {
        return await this.request('/api/subscriptions/cancel/', {
            method: 'POST',
        });
    }

    async getPaymentHistory() {
        return await this.request('/api/subscriptions/history/');
    }

    // Dev mode: activate subscription without payment
    async devActivateSubscription(planType = 'yearly') {
        return await this.request('/api/subscriptions/dev-activate/', {
            method: 'POST',
            body: JSON.stringify({ plan_type: planType }),
        });
    }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService; 